import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { checkForNewBuild } from "@/serviceWorkerRegistration";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("rxp_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("rxp_token");
        localStorage.removeItem("rxp_user");
      })
      .finally(() => setLoading(false));
  }, []);

  /**
   * login() → either:
   *   { kind: "logged_in", user }              (single-hospital, token issued)
   *   { kind: "select_hospital", memberships, pendingToken, user }
   */
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.requires_hospital_selection) {
      return {
        kind: "select_hospital",
        memberships: data.memberships || [],
        pendingToken: data.pending_token,
        user: data.user,
      };
    }
    localStorage.setItem("rxp_token", data.access_token);
    localStorage.setItem("rxp_user", JSON.stringify(data.user));
    if (data?.user?.hospital_id) {
      localStorage.setItem("rxp_last_hospital", data.user.hospital_id);
    }
    setUser(data.user);
    // On every login, make sure we're running the latest deployed build.
    checkForNewBuild();
    return { kind: "logged_in", user: data.user };
  };

  const selectHospital = async (pendingToken, hospitalId) => {
    const { data } = await api.post(
      "/auth/select-hospital",
      { hospital_id: hospitalId },
      { headers: { Authorization: `Bearer ${pendingToken}` } }
    );
    localStorage.setItem("rxp_token", data.access_token);
    localStorage.setItem("rxp_user", JSON.stringify(data.user));
    if (data?.user?.hospital_id) {
      localStorage.setItem("rxp_last_hospital", data.user.hospital_id);
    }
    setUser(data.user);
    // On every login, make sure we're running the latest deployed build.
    checkForNewBuild();
    return data.user;
  };

  const switchHospital = async (hospitalId) => {
    // For users already logged in, switch the active hospital using their current token.
    const token = localStorage.getItem("rxp_token");
    if (!token) throw new Error("Not logged in");
    const { data } = await api.post(
      "/auth/select-hospital",
      { hospital_id: hospitalId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    localStorage.setItem("rxp_token", data.access_token);
    localStorage.setItem("rxp_user", JSON.stringify(data.user));
    if (data?.user?.hospital_id) {
      localStorage.setItem("rxp_last_hospital", data.user.hospital_id);
    }
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("rxp_token");
    localStorage.removeItem("rxp_user");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, selectHospital, switchHospital, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
